(ns hai.tags.combobox
  "Creatable, entity-agnostic tag multi-select. Searches the current user's own
   tags (`:user/tags-search`) and lets you create a new tag inline by typing a
   name and picking the \"Create …\" row. Emits `{:tag-uuids [...] :tag-names [...]}`
   via `:on-change` — the caller decides which mutation to fold them into.

   The results list renders in a Radix `Popover` (portalled out of the DOM), so it
   is not clipped by an enclosing Radix Dialog's scroll box and clicking a row does
   not dismiss the dialog. Mirrors the `block-search-chip` pattern in
   `hai.builder-ide.types.block.ui`: keyboard cursor (`!index`), arrow-key nav,
   Enter-to-pick, and a `.cursor` highlight on the active row."
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.tags :as tag-ui]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private close-icon (icons/mui-icon :outlined :close))

(def ^:private debounce-ms 200)

;; Separate styled component so its CSS still applies once the Popover portals
;; the list out of the combobox's DOM subtree.
(o/defstyled tag-results :div
  :flex :flex-col :overflow-y-auto
  {:min-width "240px" :max-height "200px" :padding "4px 0"}
  [:.row :cursor-pointer {:padding "8px 10px" :font-size "0.875rem"}
   [#{:&:hover :&.cursor} {:background t/--card-forest-06}]]
  [:.create-row {:font-weight "500" :color t/--card-forest}]
  ;; An already-attached entry (a space the item is already in) stays visible so
  ;; the user can see it exists, but reads as bold and does nothing on click.
  [:.row.disabled {:font-weight "700" :cursor "default"}
   [#{:&:hover :&.cursor} {:background "transparent"}]]
  ([{:keys [entries index]}]
   (into [:<>]
         (map-indexed
          (fn [i {:keys [key label create? disabled? pick]}]
            ^{:key key}
            [:div {:class (str "row"
                               (when create? " create-row")
                               (when disabled? " disabled")
                               (when (= i index) " cursor"))
                   :on-mouse-down (fn [^js e] (.preventDefault e) (when pick (pick)))}
             label])
          entries))))

;; Search-and-create input. Emits one tag per pick via `on-pick` — either an
;; existing `{:group/uuid :group/display-name}` map or a new `{:tag/name "..."}`.
;; `exclude-uuids`/`exclude-names` hide already-attached tags from the results and
;; suppress the "Create …" row for a name that is already present. The caller owns
;; what "attached" means, so this component holds no selection of its own.
(o/defstyled tag-search :div
  :relative :w-full
  ;; Compact by default (the sticky, where everything is small); `comfortable?`
  ;; bumps it to full form-field size for dialogs — mirrors `link-adder` sizing.
  [:.input
   {:width "100%" :box-sizing "border-box"
    :padding "4px 8px"
    :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
    :border-radius "6px" :outline "none"
    :background "rgba(255,255,255,0.6)"
    :font-family "inherit" :font-size "12px"
    :color t/--charcoal}
   [:&:focus {:border-color t/--note-sage-80}]
   [:&::placeholder {:color t/--note-sage-55}]]
  [:&.comfortable
   [:.input {:padding "8px" :font-size "0.875rem" :border-radius "8px"}]]
  ;; Input shares a row with the optional cancel ✕ (host-collapse, like
  ;; `link-adder`); the input flexes, the ✕ keeps its size.
  [:.search-row
   {:display "flex" :align-items "center" :gap "6px"}
   [:.input {:flex "1" :min-width "0" :width "auto"}]]
  [:.search-cancel
   {:flex "0 0 auto"
    :width "22px" :height "22px" :padding "0"
    :border "none" :background "transparent" :border-radius "99px"
    :color t/--note-sage-80 :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"}
   [:&:hover {:color t/--card-forest :background t/--note-kraft-10}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.anchor :w-0 :h-0]

  ([{:keys [on-pick placeholder exclude-uuids exclude-names auto-focus? comfortable? on-cancel
            include-spaces? exclude-space-uuids]}]
   (r/with-let [!text     (r/atom "")
                !results  (r/atom [])
                !spaces   (r/atom [])
                !open?    (r/atom false)
                !index    (r/atom 0)
                !timer    (atom nil)
                ;; The tag typeahead always searches tags; when `include-spaces?`
                ;; it also reuses `:spaces/table` (search param) so the one field
                ;; offers both tags and spaces.
                search!   (fn [text]
                            (>e [:resolve
                                 (cond-> [{(list :user/tags-search {:tag/search-text text})
                                           [:group/uuid :group/display-name]}]
                                   include-spaces?
                                   (conj {(list :spaces/table {:search text})
                                          [{:collection/items [:space/uuid :space/title]}]}))
                                 {:on-success (fn [resp]
                                                (reset! !results (get resp :user/tags-search []))
                                                (reset! !spaces (get-in resp [:spaces/table :collection/items] []))
                                                (reset! !index 0)
                                                (reset! !open? true))}]))
                schedule! (fn [text]
                            (when-let [t @!timer] (js/clearTimeout t))
                            (reset! !timer (js/setTimeout #(search! text) debounce-ms)))]
     (let [text         @!text
           ex-uuids     (set exclude-uuids)
           ex-names     (set exclude-names)
           ex-space     (set exclude-space-uuids)
           trimmed      (str/trim text)
           results      (remove #(contains? ex-uuids (:group/uuid %)) @!results)
           ;; Spaces the item is already in stay in the list (see `disabled?`
           ;; below), so don't filter them out — a space the user knows exists
           ;; being absent reads as a bug.
           spaces       @!spaces
           exact?       (some #(= (str/lower-case (:group/display-name %))
                                  (str/lower-case trimmed))
                              @!results)
           show-create? (and (seq trimmed)
                             (not exact?)
                             (not (contains? ex-names (str/lower-case trimmed))))
           ;; `picked` is a tag map ({:group/uuid …} or {:tag/name …}) or a space
           ;; map ({:space/uuid …}); the host routes on shape.
           pick!        (fn [picked]
                          (on-pick picked)
                          (reset! !text "")
                          (reset! !results [])
                          (reset! !spaces [])
                          (reset! !open? false)
                          (reset! !index 0))
           entries      (-> (cond-> (mapv (fn [t]
                                            {:key    (:group/uuid t)
                                             :label  (tag-ui/with-hash (:group/display-name t))
                                             :create? false
                                             :pick   #(pick! t)})
                                          results)
                              show-create? (conj {:key    "__create__"
                                                  :label  (str "Create " (tag-ui/with-hash trimmed))
                                                  :create? true
                                                  :pick   #(pick! {:tag/name trimmed})}))
                            (into (map (fn [s]
                                         (let [already-in? (contains? ex-space (:space/uuid s))]
                                           {:key    (:space/uuid s)
                                            :label  [:span {:style {:display "inline-flex"
                                                                    :align-items "center"
                                                                    :gap "6px" :font-size "16px"}}
                                                     [icons/outlined-workspaces]
                                                     [:span {:style {:font-size "0.875rem"}}
                                                      (:space/title s)]]
                                            :create? false
                                            ;; Already-in spaces show bold and don't pick.
                                            :disabled? already-in?
                                            :pick   (when-not already-in? #(pick! s))}))
                                       spaces)))
           index        (max 0 (min @!index (dec (count entries))))
           open?        (and @!open? (seq entries))]
       [:<>
        {:class (when comfortable? "comfortable")}
        [c/PopoverRoot
         {:open (boolean open?)}
         [:div.search-row
          [:input.input
           {:value       text
            :placeholder (or placeholder "Add tags...")
            :auto-focus  (boolean auto-focus?)
            :on-change   (fn [^js e]
                           (let [v (.. e -target -value)]
                             (reset! !text v)
                             (reset! !index 0)
                             (schedule! v)))
            :on-focus    (fn [_] (search! @!text))
            ;; Clicking off cancels — typed text is discarded and the section
            ;; collapses; creating a tag stays behind an explicit act (Enter or
            ;; the create row). Rows pick on mouse-down with `preventDefault`,
            ;; so blur can't beat a click on a result.
            :on-blur     (fn [_]
                           (reset! !open? false)
                           (when on-cancel (on-cancel)))
            :on-key-down (fn [^js e]
                           (case (.-key e)
                             "ArrowDown" (do (.preventDefault e)
                                             (reset! !index (min (inc index) (dec (count entries)))))
                             "ArrowUp"   (do (.preventDefault e)
                                             (reset! !index (max 0 (dec index))))
                             ;; First Escape closes the dropdown; with it already
                             ;; closed, Escape collapses the section (like links).
                             "Escape"    (if @!open?
                                           (reset! !open? false)
                                           (when on-cancel (on-cancel)))
                             "Enter"     ;; let ⌘/Ctrl+Enter bubble to the modal's save shortcut
                             (when-not (or (.-ctrlKey e) (.-metaKey e))
                               (when (and open? (seq entries))
                                 (.preventDefault e)
                                 (when-let [pick (:pick (nth entries index))]
                                   (pick))))
                             nil))}]
          (when on-cancel
            [:button.search-cancel
             {:type "button"
              :aria-label "Cancel adding tag"
              :title "Cancel"
              :on-mouse-down #(.preventDefault %)
              :on-click (fn [_] (on-cancel))}
             [close-icon]])]
         [c/PopoverTrigger [:div.anchor]]
         [c/PopoverContent
          {:onOpenAutoFocus (fn [^js e] (.preventDefault e))
           :side "bottom"
           :align "start"
           :sideOffset 4}
          [c/Inset
           [tag-results {:entries entries :index index}]]]]])
     (finally
       (when-let [t @!timer] (js/clearTimeout t))))))

(defn search-slot
  "Adapts `tag-search` to `hai.entity.sections`' `:render-tag-search` — returns it
  as an element so it keeps its own state, and is a stable reference so hosts can
  pass it directly."
  [props]
  [tag-search props])

;; Multi-select for modal forms: holds its own selection, renders chips, and
;; emits the full `{:tag-uuids :tag-names}` set via `on-change` on every change.
(o/defstyled combobox :div
  :w-full
  [:.pills :flex :flex-wrap {:gap "4px" :margin-bottom "6px"}]
  ([{:keys [on-change placeholder initial-selected]}]
   (r/with-let [!selected (r/atom (vec (or initial-selected [])))
                notify!   (fn []
                            (on-change {:tag-uuids (vec (keep :group/uuid @!selected))
                                        :tag-names (vec (keep :tag/name @!selected))}))
                add!      (fn [tag] (swap! !selected conj tag) (notify!))
                remove!   (fn [tag]
                            (swap! !selected (fn [s] (vec (remove #(= % tag) s))))
                            (notify!))]
     (let [selected @!selected]
       [:<>
        (when (seq selected)
          [:div.pills
           (for [tag selected]
             ^{:key (or (:group/uuid tag) (str "new-" (:tag/name tag)))}
             [tag-ui/chip {:label     (tag-ui/with-hash (or (:group/display-name tag) (:tag/name tag)))
                           :on-remove #(remove! tag)}])])
        [tag-search
         {:placeholder   (or placeholder "Add tags...")
          :comfortable?  true
          :exclude-uuids (keep :group/uuid selected)
          :exclude-names (keep #(some-> (:tag/name %) str/lower-case) selected)
          :on-pick       add!}]]))))
